import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';

/**
 * `<RequireAuth>` — route guard for the SPA's three authenticated
 * surfaces per Requirement 5.9: `/profile`, `/sign-up`, and the
 * newsletter opt-in surface (Requirement 7). Wrapped around the
 * route element in the route tree (task 7.8 + task 8.6) so the SPA
 * gates only those three surfaces and **never** any content route.
 *
 * Behavior:
 *
 *  - When `state.auth.status === 'authenticated'` (the truthy check
 *    per `auth-slice.ts` task 7.5), renders `children` unchanged.
 *  - Otherwise, builds `next = location.pathname + location.search`
 *    and redirects to `/sign-in?next=<encodeURIComponent(next)>` via
 *    `<Navigate replace />`. The `replace` keeps the protected URL
 *    out of the browser's back-forward stack so the back button
 *    after sign-in lands on the previous page rather than the
 *    `/sign-in` interstitial.
 *
 * The `next` query param is read by `<SignInPage>` (task 7.6) and
 * threaded through the OAuth state so `<OAuthCallbackPage>` (task
 * 7.4) can navigate the user to the originally-requested URL after
 * the token exchange succeeds.
 *
 * Per Requirement 5.9, this guard MUST NOT be applied to any
 * content route. The route tree wires it only on `/profile`,
 * `/sign-up`, and `/newsletter` per design §3.3 / task 8.6.
 *
 * _Validates: Requirement 5.9 (auth-gates-only-three-surfaces),
 * Requirement 7 (newsletter opt-in surface)._
 */
export default function RequireAuth({ children }: { readonly children: ReactNode }): JSX.Element {
  const isAuthenticated = useAppSelector((state) => state.auth.status === 'authenticated');
  const location = useLocation();

  if (!isAuthenticated) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/sign-in?next=${encodeURIComponent(next)}`} replace />;
  }

  return <>{children}</>;
}
